import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Edit2, Trash2, Filter, X, AlertTriangle, Package, Camera, Upload, Printer, Sparkles } from 'lucide-react';
import AIDetectDialog from '../components/AIDetectDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONDITION_OPTIONS = [
  { value: 'good', label: 'Good', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
  { value: 'fair', label: 'Fair', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
  { value: 'needs_repair', label: 'Needs Repair', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' },
];

const Props = () => {
  const { getAuthHeaders, isManager, token } = useAuth();
  const [props, setProps] = useState([]);
  const [categories, setCategories] = useState([]);
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterContainer, setFilterContainer] = useState('');
  const [filterCondition, setFilterCondition] = useState('');
  
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAIDetect, setShowAIDetect] = useState(false);
  const [selectedProp, setSelectedProp] = useState(null);
  const [similarProps, setSimilarProps] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    container_id: '',
    quantity: 1,
    condition: 'good',
    show_production: '',
    photo_path: '',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterCategory && filterCategory !== 'all') params.append('category_id', filterCategory);
      if (filterContainer && filterContainer !== 'all') params.append('container_id', filterContainer);
      if (filterCondition && filterCondition !== 'all') params.append('condition', filterCondition);

      const [propsRes, catsRes, containersRes] = await Promise.all([
        axios.get(`${API_URL}/api/props?${params}`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/prop-categories`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/containers`, { headers: getAuthHeaders() })
      ]);
      
      setProps(propsRes.data);
      setCategories(catsRes.data);
      setContainers(containersRes.data);
    } catch (error) {
      toast.error('Failed to fetch props');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, search, filterCategory, filterContainer, filterCondition]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(debounce);
  }, [fetchData]);

  const checkSimilar = async (name) => {
    if (name.length < 3 || selectedProp) {
      setSimilarProps([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/props/similar?name=${encodeURIComponent(name)}`, { headers: getAuthHeaders() });
      setSimilarProps(response.data.similar || []);
    } catch (error) {
      setSimilarProps([]);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (formData.name && !selectedProp) {
        checkSimilar(formData.name);
      }
    }, 500);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line
  }, [formData.name]);

  const handleAIDetected = (detected) => {
    // Find matching category by name
    const matchedCategory = categories.find(c => 
      c.name.toLowerCase() === (detected.category_suggestion || '').toLowerCase()
    );
    
    setSelectedProp(null);
    setPhotoUrl(null);
    setSimilarItems([]);
    setFormData({
      name: detected.name || '',
      description: detected.description || '',
      category_id: matchedCategory?.id || '',
      container_id: '',
      quantity: 1,
      condition: detected.condition || 'good',
      show_production: '',
      photo_path: '',
      notes: ''
    });
    setShowDialog(true);
  };

  const handleOpenDialog = (prop = null) => {
    setSimilarProps([]);
    setPhotoUrl(null);
    if (prop) {
      setSelectedProp(prop);
      setFormData({
        name: prop.name,
        description: prop.description || '',
        category_id: prop.category_id || '',
        container_id: prop.container_id || '',
        quantity: prop.quantity,
        condition: prop.condition || 'good',
        show_production: prop.show_production || '',
        photo_path: prop.photo_path || '',
        notes: prop.notes || ''
      });
      if (prop.photo_path) {
        loadPhotoUrl(prop.photo_path);
      }
    } else {
      setSelectedProp(null);
      setFormData({
        name: '', description: '', category_id: '', container_id: '',
        quantity: 1, condition: 'good', show_production: '', photo_path: '', notes: ''
      });
    }
    setShowDialog(true);
  };

  const loadPhotoUrl = async (path) => {
    if (!path) return;
    try {
      const response = await axios.get(`${API_URL}/api/files/${path}?auth=${token}`, {
        responseType: 'blob'
      });
      const blobUrl = URL.createObjectURL(response.data);
      setPhotoUrl(blobUrl);
    } catch (error) {
      console.error('Failed to load photo:', error);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/api/upload/photo`, uploadData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData({ ...formData, photo_path: response.data.path });
      await loadPhotoUrl(response.data.path);
      toast.success('Photo uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        category_id: formData.category_id || '',
        container_id: formData.container_id || ''
      };
      if (selectedProp) {
        await axios.put(`${API_URL}/api/props/${selectedProp.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Prop updated successfully');
      } else {
        await axios.post(`${API_URL}/api/props`, payload, { headers: getAuthHeaders() });
        toast.success('Prop created successfully');
      }
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save prop');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/props/${selectedProp.id}`, { headers: getAuthHeaders() });
      toast.success('Prop deleted successfully');
      setShowDeleteDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete prop');
    }
  };

  const clearFilters = () => {
    setFilterCategory('');
    setFilterContainer('');
    setFilterCondition('');
  };

  const hasFilters = filterCategory || filterContainer || filterCondition;

  const getConditionBadge = (condition) => {
    const config = CONDITION_OPTIONS.find(c => c.value === condition) || CONDITION_OPTIONS[0];
    return <Badge variant="secondary" className={config.color}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="props-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Theatre Props</h1>
          <p className="text-muted-foreground mt-1">Manage your props inventory to avoid duplicates</p>
        </div>
        {isManager() && (
          <div className="flex gap-2">
            <Button variant="outline" data-testid="ai-detect-prop-btn" onClick={() => setShowAIDetect(true)}>
              <Sparkles className="w-4 h-4 mr-2" />
              AI Detect
            </Button>
            <Button data-testid="add-prop-btn" onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Prop
            </Button>
          </div>
        )}
      </div>

      {/* Search & Filters */}
      <Card className="border-border/50">
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="props-search"
                placeholder="Search by name, description, or production..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[160px]"><Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={filterContainer} onValueChange={setFilterContainer}>
                <SelectTrigger className="w-[160px]"><SelectValue placeholder="Container" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Containers</SelectItem>
                  {containers.map((c) => <SelectItem key={c.id} value={c.id}>{c.label} - {c.name}</SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={filterCondition} onValueChange={setFilterCondition}>
                <SelectTrigger className="w-[140px]"><SelectValue placeholder="Condition" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {CONDITION_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>

              {hasFilters && (
                <Button variant="ghost" size="icon" onClick={clearFilters}><X className="w-4 h-4" /></Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Props Grid */}
      {props.length === 0 ? (
        <Card className="border-border/50">
          <CardContent className="py-16 text-center">
            <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No props found. Add your first prop to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {props.map((prop) => (
            <PropCard 
              key={prop.id} 
              prop={prop} 
              token={token}
              onEdit={() => handleOpenDialog(prop)}
              onDelete={() => { setSelectedProp(prop); setShowDeleteDialog(true); }}
              isManager={isManager()}
              getConditionBadge={getConditionBadge}
            />
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedProp ? 'Edit Prop' : 'Add New Prop'}</DialogTitle>
            <DialogDescription>
              {selectedProp ? 'Update the prop details below.' : 'Fill in the details for the new prop.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                data-testid="prop-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Wooden Sword"
              />
              {similarProps.length > 0 && !selectedProp && (
                <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-600 dark:text-amber-400">Similar props found:</p>
                      <ul className="mt-1 text-sm text-muted-foreground space-y-1">
                        {similarProps.map((p) => (
                          <li key={p.id}>• {p.name} (Qty: {p.quantity})</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                data-testid="prop-description-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the prop..."
                rows={2}
              />
            </div>

            {/* Photo Upload */}
            <div className="grid gap-2">
              <Label>Photo</Label>
              <div className="flex items-center gap-3">
                {photoUrl ? (
                  <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-border">
                    <img src={photoUrl} alt="Prop" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-lg border-2 border-dashed border-border flex items-center justify-center">
                    <Camera className="w-6 h-6 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    id="photo-upload"
                    data-testid="photo-upload-input"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => document.getElementById('photo-upload').click()}
                    disabled={uploading}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? 'Uploading...' : (photoUrl ? 'Change Photo' : 'Upload Photo')}
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Category</Label>
                <Select value={formData.category_id} onValueChange={(v) => setFormData({ ...formData, category_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="prop-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Category</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Container</Label>
                <Select value={formData.container_id} onValueChange={(v) => setFormData({ ...formData, container_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="prop-container-select"><SelectValue placeholder="Select container" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Container</SelectItem>
                    {containers.map((c) => <SelectItem key={c.id} value={c.id}>{c.label} - {c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="quantity">Quantity *</Label>
                <Input
                  id="quantity"
                  data-testid="prop-quantity-input"
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="grid gap-2">
                <Label>Condition</Label>
                <Select value={formData.condition} onValueChange={(v) => setFormData({ ...formData, condition: v })}>
                  <SelectTrigger data-testid="prop-condition-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONDITION_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="show_production">Show/Production</Label>
              <Input
                id="show_production"
                data-testid="prop-show-input"
                value={formData.show_production}
                onChange={(e) => setFormData({ ...formData, show_production: e.target.value })}
                placeholder="e.g., Romeo and Juliet 2024"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button data-testid="save-prop-btn" onClick={handleSave} disabled={!formData.name}>
              {selectedProp ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Prop</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedProp?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Detect Dialog */}
      <AIDetectDialog
        open={showAIDetect}
        onClose={() => setShowAIDetect(false)}
        detectionType="prop"
        onDetected={handleAIDetected}
      />
    </div>
  );
};

// Prop Card Component
const PropCard = ({ prop, token, onEdit, onDelete, isManager, getConditionBadge }) => {
  const [photoUrl, setPhotoUrl] = useState(null);

  useEffect(() => {
    if (prop.photo_path) {
      axios.get(`${API_URL}/api/files/${prop.photo_path}?auth=${token}`, {
        responseType: 'blob'
      }).then(response => {
        setPhotoUrl(URL.createObjectURL(response.data));
      }).catch(console.error);
    }
    return () => {
      if (photoUrl) URL.revokeObjectURL(photoUrl);
    };
    // eslint-disable-next-line
  }, [prop.photo_path, token]);

  return (
    <Card className="border-border/50 overflow-hidden hover:border-primary/30 transition-colors" data-testid={`prop-card-${prop.id}`}>
      {photoUrl && (
        <div className="h-32 sm:h-40 bg-muted relative">
          <img src={photoUrl} alt={prop.name} className="w-full h-full object-cover" />
          <div className="absolute top-2 right-2">
            {getConditionBadge(prop.condition)}
          </div>
        </div>
      )}
      <CardContent className="p-3 sm:p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold truncate">{prop.name}</h3>
            {prop.description && (
              <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{prop.description}</p>
            )}
          </div>
          {!photoUrl && (
            <div className="flex-shrink-0">
              {getConditionBadge(prop.condition)}
            </div>
          )}
        </div>
        
        <div className="flex items-center justify-between text-sm gap-2">
          <span className="text-muted-foreground">Qty: <strong className="text-foreground">{prop.quantity}</strong></span>
          {prop.container_label && (
            <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono truncate">{prop.container_label}</code>
          )}
        </div>
        
        {prop.show_production && (
          <p className="text-xs text-muted-foreground italic truncate">{prop.show_production}</p>
        )}

        {isManager && (
          <div className="flex gap-1 pt-2 border-t border-border">
            <Button variant="ghost" size="sm" className="flex-1" onClick={onEdit} data-testid={`edit-prop-${prop.id}`}>
              <Edit2 className="w-3 h-3 mr-1" />
              Edit
            </Button>
            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={onDelete} data-testid={`delete-prop-${prop.id}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Props;
